import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Volume2, Star, Clock } from 'lucide-react'
import { useGame } from '../context/GameContext'
import { useGameSession, formatTime } from '../context/GameSessionContext'
import { playCorrectSound, playWrongSound, unlockAudio, speakChinese, playGameStartSound } from '../utils/sounds'
import GameComplete from '../components/GameComplete'
import { getRandomWords } from '../data/vocabulary'
import { Vocabulary, Category } from '../data/types'
import { MasterGameStat } from '../context/GameSessionContext'
import { scoreForCorrect } from '../utils/scoring'

const REQUIRED_CORRECT = 10

export default function GameAudioWord() {
  const { updateProgress } = useGame()
  const { session, addScore, pauseTimer, resumeTimer, completeMasterGame } = useGameSession()
  const requiredCorrect = session.isMasterMode ? session.masterTotalWords : REQUIRED_CORRECT
  const navigate = useNavigate()
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([])
  const [currentWord, setCurrentWord] = useState<Vocabulary | null>(null)
  const [options, setOptions] = useState<Vocabulary[]>([])
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)
  const [correctCount, setCorrectCount] = useState(0)
  const [showResult, setShowResult] = useState(false)
  const [streak, setStreak] = useState(0)
  const [hasPlayed, setHasPlayed] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [gameComplete, setGameComplete] = useState(false)
  const [totalAnswered, setTotalAnswered] = useState(0)
  const isGeneratingRef = useRef(false)
  const usedWordIds = useRef<number[]>([])
  const correctWordIds = useRef<Set<number>>(new Set())

  // Load categories once on mount
  useEffect(() => {
    unlockAudio()
    playGameStartSound()
    const stored = sessionStorage.getItem('selectedCategories')
    if (stored) {
      setSelectedCategories(JSON.parse(stored))
      setIsInitialized(true)
    } else {
      navigate('/')
    }
  }, [navigate])

  // Check if session is active
  useEffect(() => {
    if (!session.isPlaying) {
      navigate('/')
    }
  }, [navigate, session.isPlaying])

  const generateQuestion = useCallback(() => {
    if (isGeneratingRef.current || selectedCategories.length === 0) return
    isGeneratingRef.current = true
    
    // In Master Mode, check if all words are already correct before generating
    if (session.isMasterMode && correctWordIds.current.size >= requiredCorrect) {
      setGameComplete(true)
      const stat: MasterGameStat = { correct: correctWordIds.current.size, total: totalAnswered, time: session.elapsedTime, score: session.totalScore, maxStreak: streak }
      completeMasterGame('audio-word', stat)
      setTimeout(() => { isGeneratingRef.current = false }, 100)
      return
    }
    
    // In Master Mode, exclude both usedWordIds and correctWordIds to prevent re-asking correct words
    const excludeIds = session.isMasterMode 
      ? [...usedWordIds.current, ...correctWordIds.current]
      : usedWordIds.current
    
    // Get correct word from remaining words (strict mode for Master Mode)
    const correctWords = getRandomWords(1, excludeIds, selectedCategories, session.isMasterMode)
    
    // If no correct words available in Master Mode, game is complete
    if (session.isMasterMode && correctWords.length === 0) {
      setGameComplete(true)
      const stat: MasterGameStat = { correct: correctWordIds.current.size, total: totalAnswered, time: session.elapsedTime, score: session.totalScore, maxStreak: streak }
      completeMasterGame('audio-word', stat)
      setTimeout(() => { isGeneratingRef.current = false }, 100)
      return
    }
    
    const correctWord = correctWords[0]
    usedWordIds.current.push(correctWord.id)
    
    // Get decoy options from full pool (non-strict) excluding only the correct word
    const decoyWords = getRandomWords(3, [correctWord.id], selectedCategories, false)
    const words = [correctWord, ...decoyWords]
    
    setCurrentWord(correctWord)
    setOptions(words.sort(() => Math.random() - 0.5))
    setSelectedOption(null)
    setIsCorrect(null)
    setShowResult(false)
    setHasPlayed(false)
    
    setTimeout(() => {
      isGeneratingRef.current = false
    }, 100)
  }, [selectedCategories, session.isMasterMode, requiredCorrect, totalAnswered, session.elapsedTime, session.totalScore, streak, completeMasterGame])

  // Generate first question when initialized
  useEffect(() => {
    if (isInitialized && selectedCategories.length > 0 && !currentWord) {
      generateQuestion()
    }
  }, [isInitialized, selectedCategories, currentWord, generateQuestion])

  const doSpeak = (text: string) => {
    speakChinese(text)
    setHasPlayed(true)
  }

  useEffect(() => {
    if (currentWord && !hasPlayed) {
      const timer = setTimeout(() => {
        doSpeak(currentWord.chinese)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [currentWord, hasPlayed])

  const handleSelect = (wordId: number) => {
    if (showResult) return
    
    setSelectedOption(wordId)
    const correct = wordId === currentWord?.id
    setIsCorrect(correct)
    setShowResult(true)
    pauseTimer()
    
    setTotalAnswered(prev => prev + 1)
    let newCorrectCount = correctCount
    if (correct) {
      playCorrectSound()
      const points = scoreForCorrect(streak)
      addScore(points, true)
      setStreak(prev => prev + 1)
      // Track unique correct words for Master Mode
      if (session.isMasterMode && currentWord) {
        correctWordIds.current.add(currentWord.id)
      }
      newCorrectCount = correctCount + 1
      setCorrectCount(newCorrectCount)
      updateProgress(wordId, true)
    } else {
      playWrongSound()
      addScore(0, false)
      setStreak(0)
      if (currentWord) {
        updateProgress(currentWord.id, false)
        // Master Mode: remove from usedWordIds so it can be re-asked
        if (session.isMasterMode) {
          usedWordIds.current = usedWordIds.current.filter(id => id !== currentWord.id)
        }
      }
    }

    const delay = correct ? 750 : 2000
    setTimeout(() => {
      // Master Mode: complete when all unique words are correct
      const uniqueCorrectCount = session.isMasterMode ? correctWordIds.current.size : newCorrectCount
      if (uniqueCorrectCount < requiredCorrect) {
        generateQuestion()
        resumeTimer()
      } else {
        if (session.isMasterMode) {
          const stat: MasterGameStat = { correct: uniqueCorrectCount, total: totalAnswered + 1, time: session.elapsedTime, score: session.totalScore, maxStreak: streak + (correct ? 1 : 0) }
          completeMasterGame('audio-word', stat)
        }
        setGameComplete(true)
      }
    }, delay)
  }

  if (!currentWord) return null

  return (
    <div className="page-shell bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 p-3 md:p-4 flex flex-col relative">
      <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 flex-shrink-0 flex-wrap gap-2">
          <Link 
            to="/" 
            className="bg-white/20 rounded-2xl p-2 md:p-3 active:scale-95 transition-transform"
          >
            <ArrowLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </Link>
          
          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
            <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5">
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-white" />
              <span className="font-bold text-base md:text-xl text-white">
                {formatTime(session.elapsedTime)}
              </span>
            </div>
            {streak > 0 && (
              <span className="bg-orange-400 text-white px-3 py-1.5 rounded-full font-bold text-sm animate-pulse">
                🔥 {streak}
              </span>
            )}
            <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:px-4 md:py-2">
              <span className="text-white font-bold text-base md:text-xl">✅ {correctCount}/{requiredCorrect}</span>
            </div>
            <div className="bg-yellow-400 rounded-2xl px-3 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5">
              <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-800" />
              <span className="text-yellow-900 font-bold text-base md:text-xl">{session.totalScore}</span>
            </div>
          </div>
        </div>

        {/* Main Game Area */}
        <div className="flex-1 flex flex-col lg:flex-row gap-3 md:gap-4 min-h-0">
          {/* Left Side - Audio */}
          <div className="lg:flex-1 bg-white rounded-3xl p-4 md:p-6 shadow-2xl flex flex-col items-center justify-center">
            <p className="text-gray-600 text-sm md:text-lg mb-4 md:mb-6">Listen and choose the correct word</p>
            
            <button
              onClick={() => doSpeak(currentWord.chinese)}
              className="w-28 h-28 md:w-40 md:h-40 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full 
                         flex items-center justify-center shadow-xl active:scale-95 
                         transition-transform mb-3 md:mb-4"
            >
              <Volume2 className="w-14 h-14 md:w-20 md:h-20 text-white" />
            </button>
            
            <p className="text-gray-500 text-sm">Tap to listen again</p>
          </div>

          {/* Right Side - Options */}
          <div className="lg:flex-1 flex flex-col min-h-0">
            <div className="grid grid-cols-2 gap-2 md:gap-3 flex-1">
              {options.map((option) => {
                const isSelected = selectedOption === option.id
                const isCorrectOption = option.id === currentWord.id
                
                let buttonClass = 'bg-white text-gray-800'
                if (showResult) {
                  if (isCorrectOption) {
                    buttonClass = 'bg-green-500 text-white animate-correct'
                  } else if (isSelected && !isCorrectOption) {
                    buttonClass = 'bg-red-500 text-white animate-wrong'
                  }
                } else if (isSelected) {
                  buttonClass = 'bg-blue-500 text-white'
                }

                return (
                  <button
                    key={option.id}
                    onClick={() => handleSelect(option.id)}
                    disabled={showResult}
                    className={`${buttonClass} rounded-2xl md:rounded-3xl p-3 md:p-4 shadow-xl active:scale-95 
                               transition-all duration-150 flex flex-col justify-center items-center min-w-0 overflow-hidden`}
                  >
                    <div className="chinese-text text-2xl md:text-4xl font-bold mb-1 text-center break-words leading-tight">{option.chinese}</div>
                    <div className="text-xs md:text-sm opacity-80 text-center truncate w-full">{option.pinyin}</div>
                    {showResult && isCorrectOption && (
                      <div className="text-xs md:text-sm mt-1 text-center break-words leading-tight">{option.english}</div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Wrong Answer - Big centered overlay for 3 seconds */}
        {showResult && !isCorrect && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 pointer-events-none">
            <div className="wrong-overlay bg-gray-900 border-2 border-red-400 rounded-3xl px-6 py-5 md:px-12 md:py-8 shadow-2xl max-w-lg w-[calc(100vw-2rem)] max-h-[80vh] overflow-auto mx-4 text-center">
              <div className="text-5xl md:text-7xl mb-3">🚫</div>
              <div className="text-xl md:text-2xl text-red-400 font-bold mb-2">Wrong!</div>
              <div className="chinese-text text-4xl md:text-6xl font-bold text-white mb-2">{currentWord.chinese}</div>
              <div className="text-xl md:text-3xl text-white/90 mb-1">{currentWord.pinyin}</div>
              <div className="text-xl md:text-3xl font-bold text-yellow-300">{currentWord.english}</div>
            </div>
          </div>
        )}

        {/* Correct Answer - Big centered overlay */}
        {showResult && isCorrect && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 pointer-events-none">
            <div className="wrong-overlay bg-green-600 border-2 border-green-300 rounded-3xl px-6 py-5 md:px-12 md:py-8 shadow-2xl max-w-lg w-[calc(100vw-2rem)] max-h-[80vh] overflow-auto mx-4 text-center">
              <div className="text-5xl md:text-7xl mb-3">✅</div>
              <div className="text-xl md:text-2xl text-green-100 font-bold mb-2">Correct!</div>
              <div className="chinese-text text-4xl md:text-6xl font-bold text-white mb-2">{currentWord.chinese}</div>
              <div className="text-xl md:text-3xl text-white/90 mb-1">{currentWord.pinyin}</div>
              <div className="text-xl md:text-3xl font-bold text-yellow-300">{currentWord.english}</div>
            </div>
          </div>
        )}
      </div>

      {/* Game Complete */}
      {gameComplete && (
        <GameComplete
          correctCount={correctCount}
          totalQuestions={totalAnswered}
          gameName="Audio Word"
        />
      )}
    </div>
  )
}
